import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { filter } from 'rxjs';

import {
  CoreEventSource,
  HotelDetailsEventSource,
  RemoveHotelSelectionFiltersOrigin,
} from '@apps/main/new-desktop/core/models';
import {
  useEditingIngredientStore,
  useHotelDetailsStore,
  useItineraryStore,
  useMetaStore,
  usePaxStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import type {
  HotelFilterMealsUpdate,
  HotelFilterUpdate,
} from '@apps/main/new-desktop/modules/hotelFilters';
import { useFerryFiltersStore } from '@apps/main/new-desktop/core/stores/filters/ferryFiltersStore';
import { useConfigurationPanelStore } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore';
import type { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useProductDetailStore } from '@apps/main/new-desktop/core/stores/productDetail/productDetailStore';

import { assertValue, isDefined, asObservable } from '@libs/utils';
import { triggerAccommodationHotjarSurvey } from '@libs/hotjar-events';
import { isAvailableHotelIngredientResponse } from '@libs/search-service-smart-search';
import { ProductSearchType } from '@libs/trip-planner-interfaces';
import type { IngredientId, Pax } from '@libs/trip-planner-interfaces';

import type {
  HotelDynamicData,
  HotelStaticData,
  SelectStayEventPayload,
  ToggleHeartEventPayload,
} from '../../../modules/hotelDetails';
import type {
  TripOverviewActivityDetailsPanel,
  TripOverviewHotelDetailsPanel,
  TripOverviewPanel,
} from '../models';
import {
  TRIP_OVERVIEW_HOTEL_FILTERS_PANEL,
  TRIP_OVERVIEW_CONFIGURATION_PANEL,
  TRIP_OVERVIEW_FLIGHTS_FILTERS_PANEL,
  TRIP_OVERVIEW_FERRY_FILTERS_PANEL,
  TRIP_OVERVIEW_NEW_TRIP_PRICE_SUMMARY_PANEL,
  TRIP_OVERVIEW_NO_PANEL,
  TripOverviewPanelType,
} from '../models';

export const useTripOverviewPanelsStore = defineStore(
  'TripOverviewPanelStore',
  () => {
    const productDetailStore = useProductDetailStore();
    const recipeStore = useRecipeStore();
    const itineraryStore = useItineraryStore();
    const statsigEventsStore = useStatsigEventsStore();
    const { selectedItinerary } = storeToRefs(itineraryStore);
    const metaStore = useMetaStore();
    const paxStore = usePaxStore();
    const hotelDetailsStore = useHotelDetailsStore();
    const configurationPanelStore = useConfigurationPanelStore();
    const editingIngredientStore = useEditingIngredientStore();
    const filtersStore = useFiltersStore();
    const ferryFiltersStore = useFerryFiltersStore();

    // State
    const tripOverviewPanel = ref<TripOverviewPanel>(TRIP_OVERVIEW_NO_PANEL);

    // Actions
    function showMetaHotelDetails(): void {
      const selectedItinerarySubscription = asObservable(selectedItinerary)
        .pipe(filter(isDefined))
        .subscribe((itinerary) => {
          const firstHotelIngredient = itinerary.hotelOnlyItinerary[0];
          assertValue(
            firstHotelIngredient,
            'tripOverviewPanelsStore.ts > showMetaHotelDetails > firstHotelIngredient'
          );

          const selectedHotelCode = isAvailableHotelIngredientResponse(
            firstHotelIngredient
          )
            ? firstHotelIngredient.suggested.code
            : undefined;
          const ingredientId = firstHotelIngredient.ingredientId;

          metaStore.metaHotelCode = selectedHotelCode;

          if (selectedHotelCode) {
            openHotelDetailsPanel(selectedHotelCode, ingredientId, true);

            selectedItinerarySubscription.unsubscribe();
          }
        });
    }

    function closeTripOverviewPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    function openHotelDetailsPanel(
      hotelCode: string,
      ingredientId: IngredientId,
      shouldScrollToAvailability = false
    ): void {
      const hotelDetailsPanel: TripOverviewHotelDetailsPanel = {
        type: TripOverviewPanelType.HotelDetails,
        hotelCode,
        ingredientId,
      };
      tripOverviewPanel.value = hotelDetailsPanel;
      hotelDetailsStore.openHotelDetails(
        hotelCode,
        ingredientId,
        shouldScrollToAvailability
      );
      const stayIngredient = recipeStore.getStayIngredient(ingredientId);

      editingIngredientStore.setStayEditingIngredient(stayIngredient);
    }

    function closeHotelDetailsPanel(): void {
      statsigEventsStore.isAlternativeHotelOpened = false;
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    function openActivityDetailsPanel(
      productCode: string,
      ingredientId: IngredientId,
      shouldScrollToAvailability = false
    ): void {
      const activityDetailsPanel: TripOverviewActivityDetailsPanel = {
        type: TripOverviewPanelType.ActivityDetails,
        hotelCode: productCode,
        ingredientId,
      };
      tripOverviewPanel.value = activityDetailsPanel;
      productDetailStore.updateShouldScrollToAvailability(
        shouldScrollToAvailability
      );
      productDetailStore.selectDestinationProduct(
        productCode,
        ingredientId,
        ProductSearchType.Activity
      );
    }

    function closeActivityDetailsPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
      productDetailStore.clearProductDetails();
    }

    function openConfigurationPanel(
      tabKey: TabKey,
      source:
        | 'overview-header-actions'
        | 'date-location-segment'
        | 'low-price-calendar'
    ): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_CONFIGURATION_PANEL;
      configurationPanelStore.handleTabSwitch(tabKey);
      statsigEventsStore.logCorePaxModalOpened({
        source: CoreEventSource.TripOverview,
      });

      statsigEventsStore.logTripOverviewConfigurationPanelOpened({
        source,
        tab: tabKey,
      });
    }

    function closeConfigurationPanel(): void {
      paxStore.clearWarningMessages();
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    function updatePax(pax: Pax): void {
      if (pax.length) {
        paxStore.updatePax(pax, CoreEventSource.TripOverview);
      }
    }

    function selectStay(eventPayload: SelectStayEventPayload): void {
      statsigEventsStore.logHotelDetailsStaySelected({
        source: statsigEventsStore.isAlternativeHotelOpened
          ? HotelDetailsEventSource.TripOverviewAlternativeHotels
          : HotelDetailsEventSource.TripOverview,
      });
      triggerAccommodationHotjarSurvey();
      hotelDetailsStore.selectStay(eventPayload);
      closeHotelDetailsPanel();
    }

    function toggleHeart(eventPayload: ToggleHeartEventPayload): void {
      statsigEventsStore.logHotelDetailsSelected({
        source: statsigEventsStore.isAlternativeHotelOpened
          ? HotelDetailsEventSource.TripOverviewAlternativeHotels
          : HotelDetailsEventSource.TripOverview,
      });
      hotelDetailsStore.toggleHeart(eventPayload);
      closeHotelDetailsPanel();
    }

    const tripOverviewPanelType = computed<TripOverviewPanelType>(
      () => tripOverviewPanel.value.type
    );

    const hotelDetailsStaticData = computed<HotelStaticData | undefined>(() => {
      if (tripOverviewPanel.value.type !== TripOverviewPanelType.HotelDetails) {
        return;
      }

      return hotelDetailsStore.hotelDetailsStaticData;
    });

    const hotelDetailsDynamicData = computed<HotelDynamicData | undefined>(
      () => {
        if (
          tripOverviewPanel.value.type !== TripOverviewPanelType.HotelDetails
        ) {
          return;
        }

        return hotelDetailsStore.hotelDetailsDynamicData;
      }
    );

    function openTripPriceSummaryPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NEW_TRIP_PRICE_SUMMARY_PANEL;
    }

    function closeTripPriceSummaryPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    function openHotelFiltersPanel(ingredientId: IngredientId): void {
      const hotelIngredient = recipeStore.getHotelIngredient(ingredientId);
      editingIngredientStore.setStayEditingIngredient(hotelIngredient);
      filtersStore.setHotelIngredientForFiltering();

      tripOverviewPanel.value = TRIP_OVERVIEW_HOTEL_FILTERS_PANEL;
    }

    function closeHotelFiltersPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    function applyHotelFilters(): void {
      const newHotelIngredient = filtersStore.getChangedHotelIngredient();
      if (newHotelIngredient && editingIngredientStore.editingStayIngredient) {
        editingIngredientStore.editingStayIngredient.filters =
          newHotelIngredient.filters;
      }
      filtersStore.clearEditedHotelIngredient();
      closeHotelFiltersPanel();
    }

    function updateHotelFilters(event: HotelFilterUpdate): void {
      filtersStore.updateHotelFilter(
        event,
        RemoveHotelSelectionFiltersOrigin.TripOverview,
        false
      );
    }

    function resetHotelFilters(): void {
      filtersStore.resetHotelFilters();
    }

    function updateMealFilter(event: {
      filter: HotelFilterMealsUpdate;
      source: RemoveHotelSelectionFiltersOrigin;
    }): void {
      filtersStore.updateHotelFilter(event.filter, event.source);
    }

    function toggleFreeCancellationFilter(isChecked: boolean): void {
      hotelDetailsStore.toggleFreeCancellationFilter(isChecked);
    }

    function openFlightsFiltersPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_FLIGHTS_FILTERS_PANEL;
    }

    function closeFlightsFiltersPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    function openFerryFiltersPanel(ingredientId: IngredientId): void {
      ferryFiltersStore.setAllowedFerryIngredientIds([ingredientId]);
      tripOverviewPanel.value = TRIP_OVERVIEW_FERRY_FILTERS_PANEL;
    }

    function closeFerryFiltersPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    // Getters
    const hotelMealFilterOptions = computed(
      () => filtersStore.getMealFitlerOptions
    );

    const hotelFreeCancellationFilter = computed<boolean>(
      () => filtersStore.getHotelFreeCancellationFilter
    );

    return {
      // Actions
      closeActivityDetailsPanel,
      closeHotelDetailsPanel,
      closeTripOverviewPanel,
      closeConfigurationPanel,
      updatePax,
      openConfigurationPanel,
      openActivityDetailsPanel,
      openHotelDetailsPanel,
      selectStay,
      showMetaHotelDetails,
      toggleHeart,
      openTripPriceSummaryPanel,
      closeTripPriceSummaryPanel,
      openHotelFiltersPanel,
      closeHotelFiltersPanel,
      applyHotelFilters,
      updateHotelFilters,
      resetHotelFilters,
      openFlightsFiltersPanel,
      closeFlightsFiltersPanel,
      openFerryFiltersPanel,
      closeFerryFiltersPanel,
      updateMealFilter,
      toggleFreeCancellationFilter,

      // Getters
      hotelDetailsStaticData,
      hotelDetailsDynamicData,
      tripOverviewPanelType,
      hotelMealFilterOptions,
      hotelFreeCancellationFilter,
    };
  }
);
